// src/components/ContactForm.js
import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser'; // Import the emailjs library
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  SimpleGrid,
  useColorModeValue,
  IconButton,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaFacebookF, FaTwitter, FaPinterestP, FaInstagram, FaYoutube } from 'react-icons/fa';

export default function ContactForm() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const formBgColor = useColorModeValue('gray.50', 'gray.700');
  const infoBgColor = 'blue.800';

  // --- NEW LOGIC START ---

  // Ref for the form element, needed by emailjs
  const form = useRef();

  // State to manage form submission status (e.g., submitting, success, error)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const sendEmail = (e) => {
    e.preventDefault(); // Prevent the default form submission (page reload)
    setIsSubmitting(true); // Show loading state on the button
    setSubmitStatus(null); // Reset status on new submission

    // Replace with your actual EmailJS Service ID, Template ID, and Public Key
    const serviceID = 'service_s1d66l9';
    const templateID = 'template_r3z5ufw';
    const publicKey = 'm7bBZCxJPse0yzWvB';

    emailjs
      .sendForm(serviceID, templateID, form.current, publicKey)
      .then(
        () => {
          console.log('SUCCESS!');
          setSubmitStatus('success');
          setIsSubmitting(false);
          form.current.reset(); // Reset the form fields after successful submission
        },
        (error) => {
          console.log('FAILED...', error.text);
          setSubmitStatus('error');
          setIsSubmitting(false);
        }
      );
  };

  // --- NEW LOGIC END ---

  const stripedBg = {
    backgroundColor: infoBgColor,
    backgroundImage: `repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05) 1px, transparent 1px, transparent 10px)`,
  };

  return (
    <Box as="section" bg={bgColor} py={{ base: 16, md: 24 }} id="contact">
      <Box maxW="7xl" mx="auto" px={8}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Text color="orange.500" fontWeight="bold">Contact Us</Text>
          <Heading as="h2" size="2xl" fontWeight="bold">
            Have a Project Idea? <Text as="span" color="orange.500">Let's Talk!</Text>
          </Heading>
        </VStack>

        <Flex direction={{ base: 'column', lg: 'row' }} gap={10}>
          {/* We now use a <form> element and pass the ref and onSubmit handler */}
          <VStack
            as="form"
            ref={form}
            onSubmit={sendEmail}
            spacing={6}
            flex="2"
            p={8}
            bg={formBgColor}
            borderRadius="lg"
            w="full"
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
              {/* Each input now requires a 'name' attribute that MUST match the template variable in EmailJS */}
              <FormControl id="first-name" isRequired>
                <FormLabel>First Name *</FormLabel>
                <Input name="first_name" placeholder="Ex. John" bg="white" />
              </FormControl>
              <FormControl id="last-name" isRequired>
                <FormLabel>Last Name *</FormLabel>
                <Input name="last_name" placeholder="Ex. Doe" bg="white" />
              </FormControl>
              <FormControl id="email" isRequired>
                <FormLabel>Email *</FormLabel>
                <Input name="email" type="email" placeholder="example@gmail.com" bg="white" />
              </FormControl>
              <FormControl id="phone-number" isRequired>
                <FormLabel>Phone Number *</FormLabel>
                <Input name="phone_number" type="tel" placeholder="Enter Phone Number" bg="white" />
              </FormControl>
            </SimpleGrid>
            <FormControl id="subject" isRequired>
              <FormLabel>Subject *</FormLabel>
              <Input name="subject" placeholder="Enter subject here..." bg="white" />
            </FormControl>
            <FormControl id="message" isRequired>
              <FormLabel>Your Message *</FormLabel>
              <Textarea name="message" placeholder="Enter here..." rows={5} bg="white" />
            </FormControl>
            <Button
              type="submit"
              colorScheme="orange"
              size="lg"
              alignSelf="flex-start"
              isLoading={isSubmitting} // Show a spinner while sending
            >
              Send Message
            </Button>

            {/* Display Success or Error Message */}
            {submitStatus === 'success' && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Your message has been sent successfully!
              </Alert>
            )}
            {submitStatus === 'error' && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                Something went wrong. Please try again later.
              </Alert>
            )}
          </VStack>

          {/* Right Side: Contact Info (No changes here) */}
          <VStack
            flex="1"
            sx={stripedBg}
            color="white"
            p={8}
            borderRadius="lg"
            spacing={8}
            align="flex-start"
            w="full"
          >
             {/* ... content is the same ... */}
             <VStack align="flex-start">
              <Heading as="h3" size="md">Address</Heading>
              <Text color="gray.300">Adama Mebrat hayl</Text>
            </VStack>
            <VStack align="flex-start">
              <Heading as="h3" size="md">Contact</Heading>
              <Text color="gray.300">Phone: +251903929292</Text>
              <Text color="gray.300">Phone: +251911499494</Text>
              <Text color="gray.300">Email: info@anbelat.com</Text>
            </VStack>
            <VStack align="flex-start">
              <Heading as="h3" size="md">Open Time</Heading>
              <Text color="gray.300">Monday - Friday: 10:00 - 20:00</Text>
              <Text color="gray.300">Saturday - Sunday: 11:00 - 18:00</Text>
            </VStack>
            <VStack align="flex-start">
              <Heading as="h3" size="md">Stay Connected</Heading>
              <HStack spacing={3}>
                <IconButton as="a" href="https://www.instagram.com/anbelat_construction/" aria-label="Instagram" icon={<FaInstagram />} isRound colorScheme='whiteAlpha' />
                <IconButton as="a" href="https://x.com/AnbelatC40568" aria-label="Twitter" icon={<FaTwitter />} isRound colorScheme='whiteAlpha' />
                <IconButton as="a" href="#" aria-label="Facebook" icon={<FaFacebookF />} isRound colorScheme='whiteAlpha' />
              </HStack>
            </VStack>
          </VStack>
        </Flex>
      </Box>
    </Box>
  );
}